/**
 * Apple signing material (decision of 2026-10-22) and the Android upload keystore (P13), api/openapi.yaml Credentials:
 * metadata for org admins and the project's maintainers; upload, rotate and revoke for platform operators (Malible staff)
 * only — the API answers 403 to everyone else. The file and its passwords travel once, as multipart parts; responses never
 * carry them.
 */
import { z } from 'zod';
import { CUSTODIED_KINDS, type CustodiedKind } from '@hangar/shared';
import { ApiError, api } from './client';
import { orgHeaders } from './orgSelection';

const enc = encodeURIComponent;

export const SigningCredential = z.object({
  id: z.string(),
  kind: z.string(),
  name: z.string(),
  projectId: z.string().nullable().optional(),
  appId: z.string().nullable().optional(),
  status: z.string().default('unknown'),
  expiresAt: z.string().nullable().optional(),
  validFrom: z.string().nullable().optional(),
  rotatedAt: z.string().nullable().optional(),
  createdAt: z.string().optional(),
  fingerprints: z.record(z.string(), z.string()).optional(),
  metadata: z.record(z.string(), z.unknown()).default({}),
  createdBy: z.object({ email: z.string() }).nullable().optional(),
});
export type SigningCredential = z.infer<typeof SigningCredential>;
const SigningCredentialList = z.object({ data: z.array(SigningCredential), nextCursor: z.string().nullable() });

const isSigning = (c: SigningCredential): boolean => (CUSTODIED_KINDS as readonly string[]).includes(c.kind);

/** The project's signing credentials, Apple and Android (every page; revoked ones left out). */
export async function listSigningCredentials(projectId: string): Promise<SigningCredential[]> {
  const out: SigningCredential[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < 20; page++) {
    const r = await api('GET', '/credentials', { schema: SigningCredentialList, query: { projectId, cursor, limit: 200 } });
    out.push(...r.data.filter(isSigning));
    if (!r.nextCursor) break;
    cursor = r.nextCursor;
  }
  return out;
}

export interface SigningUpload {
  file: File;
  /** `.p12` only. */
  password?: string;
  /** `.p8` only. */
  keyId?: string;
  issuerId?: string;
  /** Android keystore only: the store password (`password` above is the `.p12`'s), the key's alias and password (empty: the store password). */
  keystorePassword?: string;
  keyAlias?: string;
  keyPassword?: string;
  /** Android keystore only: the package name, when the app has no Google Play store app to read it from. */
  applicationId?: string;
  acknowledgeCertificateChange?: boolean;
}

/** A multipart call with the session's bearer, read as a credential (the console's createTokenCredential pattern). */
export async function multipart(path: string, form: FormData, idempotencyKey?: string): Promise<SigningCredential> {
  const { currentIdToken, dropSession } = await import('../auth/userManager');
  const { config } = await import('../config');
  const bearer = await currentIdToken();
  if (!bearer) {
    void dropSession('expired');
    throw new ApiError(401, 'HGR-AUTH-REQUIRED', 'Not signed in');
  }
  const res = await fetch(new URL(`/v1${path}`, `${config.apiUrl}/`), {
    method: 'POST',
    headers: { Authorization: `Bearer ${bearer}`, Accept: 'application/json, application/problem+json', ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}), ...orgHeaders() },
    body: form,
    cache: 'no-store',
  });
  const json: unknown = await res.json().catch(() => null);
  if (res.status === 401) void dropSession('unauthorized');
  if (!res.ok) {
    const b = (typeof json === 'object' && json !== null ? json : {}) as Record<string, unknown>;
    const message = typeof b.message === 'string' ? b.message : typeof b.detail === 'string' ? b.detail : `Request failed (${res.status})`;
    throw new ApiError(res.status, typeof b.code === 'string' ? b.code : `HTTP-${res.status}`, message, typeof b.requestId === 'string' ? b.requestId : undefined, [], b.details);
  }
  return SigningCredential.parse(json);
}

function formOf(metadata: Record<string, unknown>, upload: SigningUpload): FormData {
  const form = new FormData();
  form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
  const secrets = upload.keystorePassword !== undefined
    ? { keystorePassword: upload.keystorePassword, keyAlias: upload.keyAlias?.trim() ?? '', ...(upload.keyPassword ? { keyPassword: upload.keyPassword } : {}) }
    : upload.password !== undefined ? { password: upload.password } : undefined;
  if (secrets) form.append('secrets', new Blob([JSON.stringify(secrets)], { type: 'application/json' }));
  form.append('file', upload.file, upload.file.name);
  return form;
}

const attributesOf = (u: SigningUpload) =>
  u.keyId || u.issuerId ? { attributes: { keyId: u.keyId?.trim(), issuerId: u.issuerId?.trim() } } : u.applicationId?.trim() ? { attributes: { applicationId: u.applicationId.trim() } } : {};

export const uploadSigningCredential = (kind: CustodiedKind, appId: string, name: string, upload: SigningUpload, idempotencyKey: string) =>
  multipart('/credentials', formOf({ kind, name, appId, platform: kind === 'android_upload_keystore' ? 'android' : 'ios', managedBy: 'hangar', acknowledgeCertificateChange: Boolean(upload.acknowledgeCertificateChange), ...attributesOf(upload) }, upload), idempotencyKey);

export const rotateSigningCredential = (credentialId: string, upload: SigningUpload) =>
  multipart(`/credentials/${enc(credentialId)}/rotate`, formOf({ acknowledgeCertificateChange: Boolean(upload.acknowledgeCertificateChange), ...attributesOf(upload) }, upload));

export const revokeSigningCredential = (credentialId: string) => api('DELETE', `/credentials/${enc(credentialId)}`, { schema: SigningCredential });
